/**
 * 🏛️ Guild Hall Ladders — Feb 27 2026 canon
 *
 * Visualizes all four owner-locked progression ladders for the Guild:
 *   • 🏪 Guild Shop Upgrades (1→10)
 *   • 🗼 Skill Tower Upgrades (1→10)
 *   • 🪄 Divine Altar / Guild Lever Upgrades (1→10)
 *   • 💸 Weekly Upkeep (1→10)
 *
 * Pulls everything from /api/guild/state — the constants block carries the
 * full ladder tables and the per-guild block carries current levels +
 * next-upgrade snapshots.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ───────── Helpers ─────────
const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");

function formatCountdown(ms) {
  if (ms <= 0) return "0s";
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function totalRemaining(ladder, currentLevel) {
  let total = 0;
  for (let lv = (currentLevel || 0) + 1; lv <= 10; lv += 1) {
    const tier = ladder?.[lv] || ladder?.[String(lv)];
    if (!tier) continue;
    total += Number(tier.upgrade_cost_wealth ?? tier.apex_wealth ?? 0);
  }
  return total;
}

function pctToMax(currentLevel) {
  return Math.min(100, Math.round(((currentLevel || 0) / 10) * 100));
}

// ───────── Reusable ladder card ─────────
function LadderCard({ icon, title, blurb, ladder, currentLevel, costKey, accent, currentWealth, testid, buildingId, canUpgrade, onUpgrade, busyBuilding, finishAt, rushOd, onRush, busyRush, nowMs, globalLockBuilding, globalLockLabel }) {
  const safeLadder = ladder || {};
  const nextLevel = (currentLevel || 0) + 1;
  const nextTier = safeLadder[nextLevel] || safeLadder[String(nextLevel)];
  const nextCost = Number(nextTier?.[costKey] ?? 0);
  const wealthGap = nextCost - (currentWealth || 0);
  const canAfford = nextCost > 0 && wealthGap <= 0;
  const remaining = totalRemaining(safeLadder, currentLevel);
  const isBusy = busyBuilding === buildingId;
  const isRushBusy = busyRush === buildingId;

  // 🚧 Under-construction state
  const finishMs = finishAt ? new Date(finishAt).getTime() : 0;
  const underConstruction = finishMs > nowMs;
  const remainingMs = Math.max(0, finishMs - nowMs);

  // 🔒 Global one-at-a-time lock — another building is mid-build, so this
  // card can't start a new upgrade either. Only fires when the OTHER
  // building is the locked one (self-lock is handled by `underConstruction`).
  const isOtherLocked = !!globalLockBuilding && globalLockBuilding !== buildingId;

  const canPress = canUpgrade && canAfford && !!nextTier && !isBusy && !underConstruction && !isOtherLocked;

  return (
    <section
      className={`rounded-2xl border ${accent.border} bg-gradient-to-br ${accent.bg} p-5`}
      data-testid={testid}
    >
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-3xl">{icon}</span>
            <h2 className={`text-lg font-black ${accent.title} tracking-wide`}>{title}</h2>
          </div>
          <p className="text-[11px] text-neutral-400 mt-1">{blurb}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Current</div>
          <div className={`text-3xl font-black ${accent.title} leading-none`}>Lv {currentLevel ?? 1}</div>
          <div className="mt-2 w-24 h-1.5 bg-black/60 rounded-full overflow-hidden">
            <div
              className={`h-full ${accent.bar} transition-all`}
              style={{ width: `${pctToMax(currentLevel)}%` }}
            />
          </div>
        </div>
      </header>

      {underConstruction && (
        <div className="rounded-xl p-4 mb-3 border-2 border-amber-400 bg-gradient-to-br from-amber-950/80 to-orange-950/60 shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)] animate-pulse-soft" data-testid={`${testid}-construction`}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🚧</span>
              <span className="text-amber-200 font-black text-base tracking-wider uppercase">Under Construction</span>
            </div>
            <span
              className="text-amber-100 font-mono text-2xl font-black tabular-nums tracking-tight bg-black/40 px-3 py-1 rounded-md border border-amber-500/50"
              data-testid={`${testid}-countdown`}
            >
              {formatCountdown(remainingMs)}
            </span>
          </div>
          {/* Visual progress bar */}
          {(() => {
            const totalMs = Number(finishMs) - Number(nowMs) + Number(remainingMs);
            const pct = totalMs > 0 ? Math.max(0, Math.min(100, 100 - (remainingMs / totalMs) * 100)) : 0;
            return (
              <div className="h-2 bg-black/60 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 via-orange-400 to-amber-300 transition-all duration-1000"
                  style={{ width: `${pct}%` }}
                />
              </div>
            );
          })()}
          <div className="text-xs text-amber-100/80">
            Lv {currentLevel} build in progress — further upgrades to this building paused until the timer ends.
          </div>
          {canUpgrade && rushOd > 0 && (
            <button
              type="button"
              onClick={() => !isRushBusy && onRush?.(buildingId, rushOd)}
              disabled={isRushBusy}
              data-testid={`${testid}-rush-btn`}
              className={`mt-3 w-full rounded-md py-2.5 text-sm font-black tracking-wider uppercase transition-all ${
                isRushBusy
                  ? "bg-amber-900/40 text-amber-500 cursor-not-allowed"
                  : "bg-amber-500 text-black hover:brightness-110 active:scale-[0.98]"
              }`}
            >
              {isRushBusy ? "⏳ Rushing…" : `⏩ Rush Now · ${rushOd} 💸 OD`}
            </button>
          )}
        </div>
      )}

      {nextTier ? (
        <div className={`rounded-lg p-3 mb-3 border ${canAfford ? "border-emerald-600/60 bg-emerald-950/30" : "border-neutral-700/60 bg-black/40"}`}>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] uppercase tracking-widest text-neutral-400">Next upgrade · Lv {nextLevel}</span>
            <span className={`text-base font-black ${canAfford ? "text-emerald-300" : "text-amber-200"}`}>
              {fmt(nextCost)} 💎
            </span>
          </div>
          <div className="text-xs text-neutral-300">{nextTier.unlock || "—"}</div>
          {!canAfford && nextCost > 0 && (
            <div className="text-[10px] text-neutral-500 mt-1">
              Need <span className="text-red-300 font-bold">{fmt(wealthGap)}</span> more 💎 in the pool
            </div>
          )}
          {canUpgrade && (
            <button
              type="button"
              onClick={() => canPress && onUpgrade?.(buildingId, nextCost, nextLevel)}
              disabled={!canPress}
              data-testid={`${testid}-upgrade-btn`}
              className={`mt-3 w-full rounded-md py-2 text-xs font-black tracking-wider uppercase transition-all ${
                canPress
                  ? `${accent.btn} hover:brightness-110 active:scale-[0.98] cursor-pointer`
                  : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
              }`}
            >
              {isBusy
                ? "⏳ Upgrading…"
                : isOtherLocked
                  ? `🔒 ${globalLockLabel} is building — wait or rush it`
                  : canAfford
                    ? `⬆ Upgrade to Lv ${nextLevel}`
                    : "Pool too low"}
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-lg p-3 mb-3 border border-fuchsia-700/50 bg-fuchsia-950/30 text-center">
          <div className="text-fuchsia-300 font-black text-sm">⭐ MAX LEVEL ⭐</div>
          <div className="text-[10px] text-neutral-400 mt-1">No further upgrades available</div>
        </div>
      )}

      <details className="group">
        <summary className="text-[11px] cursor-pointer text-neutral-400 hover:text-neutral-200 select-none">
          ▸ Full ladder · {fmt(remaining)} 💎 remaining to max
        </summary>
        <div className="mt-2 space-y-1">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((lv) => {
            const tier = safeLadder[lv] || safeLadder[String(lv)];
            if (!tier) return null;
            const cost = Number(tier[costKey] ?? 0);
            const isCurrent = lv === currentLevel;
            const isReached = lv <= (currentLevel || 0);
            return (
              <div
                key={lv}
                className={`flex items-center justify-between text-[11px] rounded px-2 py-1 ${
                  isCurrent ? "bg-amber-900/40 border border-amber-500/50 font-bold" :
                  isReached ? "bg-emerald-900/20 text-emerald-300" :
                  "bg-black/30 text-neutral-400"
                }`}
                data-testid={`${testid}-row-${lv}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className={`inline-block w-5 text-center font-mono ${isCurrent ? "text-amber-200" : ""}`}>
                    {isReached ? "✓" : lv}
                  </span>
                  <span className="truncate">{tier.unlock || `Lv ${lv}`}</span>
                </span>
                <span className="font-mono shrink-0 ml-2">
                  {lv === 1 && cost === 0 ? "—" : `${fmt(cost)}`}
                </span>
              </div>
            );
          })}
        </div>
      </details>
    </section>
  );
}

// ───────── Upkeep card (different shape — flat lookup, no upgrade gating) ─────────
function UpkeepCard({ upkeepTable, currentLevel, currentWealth, frozen, testid }) {
  const safeTable = upkeepTable || {};
  const weeklyCost = Number(safeTable[currentLevel] ?? safeTable[String(currentLevel)] ?? 0);
  const weeksOfCushion = weeklyCost > 0 ? Math.floor((currentWealth || 0) / weeklyCost) : "∞";
  const isDanger = !frozen && typeof weeksOfCushion === "number" && weeksOfCushion < 2;

  return (
    <section
      className="rounded-2xl border border-rose-700/60 bg-gradient-to-br from-rose-950/40 via-black to-rose-950/20 p-5"
      data-testid={testid}
    >
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-3xl">💸</span>
            <h2 className="text-lg font-black text-rose-200 tracking-wide">Weekly Upkeep</h2>
            {frozen && (
              <span data-testid="upkeep-frozen-badge"
                    className="px-2 py-0.5 rounded-full border border-cyan-500/60 bg-cyan-950/50 text-[10px] font-black text-cyan-300 tracking-widest">
                🧊 FROZEN
              </span>
            )}
          </div>
          <p className="text-[11px] text-neutral-400 mt-1">
            {frozen
              ? "Rent is currently FROZEN — no Wealth is being drained. The ladder below stays for reference."
              : "Drains Guild Wealth each week — scales with Hall level. Higher halls are harder to maintain."}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Current Hall</div>
          <div className="text-3xl font-black text-rose-200 leading-none">Lv {currentLevel ?? 1}</div>
        </div>
      </header>

      <div className={`rounded-lg p-3 mb-3 border ${isDanger ? "border-red-700 bg-red-950/40" : "border-rose-800/60 bg-black/40"}`}>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-[10px] uppercase tracking-widest text-neutral-400">This week&apos;s cost</span>
          <span className="text-base font-black text-rose-200">{fmt(weeklyCost)} 💎 / week</span>
        </div>
        <div className="text-xs text-neutral-300">
          Cushion at current wealth: <span className={`font-bold ${isDanger ? "text-red-300" : "text-emerald-300"}`}>
            {weeksOfCushion} week{weeksOfCushion === 1 ? "" : "s"}
          </span>
        </div>
        {isDanger && (
          <div className="text-[10px] text-red-300 mt-1 font-bold">
            ⚠️ DANGER ZONE — contribute Wealth fast or risk decay.
          </div>
        )}
      </div>

      <details className="group">
        <summary className="text-[11px] cursor-pointer text-neutral-400 hover:text-neutral-200 select-none">
          ▸ Full upkeep ladder (Lv 1 → Lv 10)
        </summary>
        <div className="mt-2 space-y-1">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((lv) => {
            const cost = Number(safeTable[lv] ?? safeTable[String(lv)] ?? 0);
            const isCurrent = lv === currentLevel;
            return (
              <div
                key={lv}
                className={`flex items-center justify-between text-[11px] rounded px-2 py-1 ${
                  isCurrent ? "bg-rose-900/40 border border-rose-500/50 font-bold" : "bg-black/30 text-neutral-400"
                }`}
                data-testid={`${testid}-row-${lv}`}
              >
                <span>Lv {lv}</span>
                <span className="font-mono">{fmt(cost)} 💎 / week</span>
              </div>
            );
          })}
        </div>
      </details>
    </section>
  );
}

// ───────── 🏛️ Main Hall card (gates all sub-buildings · paid in personal Chits) ─────────
function HallCard({ hallLevel, towerLadder, canUpgrade, busy, onUpgrade, poolWealth = 0 }) {
  const lv = Number(hallLevel || 1);
  const maxed = lv >= 10;
  const next = lv + 1;
  const nextTier = (towerLadder || {})[next] || (towerLadder || {})[String(next)] || {};
  // 💎 CANON Mar 2026 — the ladder number IS the Wealth cost directly.
  // Owner: "1,000 Chits = 1 Wealth. Do NOT divide — that made upgrades cheap."
  const costWealth = Number(nextTier.guild_chits || 0);
  const affordable = poolWealth >= costWealth;

  return (
    <section
      className="rounded-2xl border-2 border-amber-600/70 bg-gradient-to-br from-amber-950/40 via-black to-amber-950/10 p-5 mb-4"
      data-testid="guild-hall-main-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-4xl">🏛️</span>
          <div>
            <h2 className="text-xl font-black text-amber-200 tracking-wide">Guild Hall (Main)</h2>
            <p className="text-[11px] text-neutral-400 mt-0.5 max-w-md">
              The Hall gates EVERY building — Shop, Skill Tower and Divine Altar can never
              exceed the Hall level. Each Hall level also adds <span className="text-amber-300 font-bold">+5 member cap</span>.
              Paid from the <span className="text-amber-300 font-bold">shared guild pool</span> (group effort).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Hall Level</div>
            <div className="text-4xl font-black text-amber-200 leading-none" data-testid="guild-hall-level">Lv {lv}</div>
          </div>
          {maxed ? (
            <div className="px-4 py-2 rounded-xl border border-emerald-600/60 bg-emerald-950/40 text-emerald-300 text-xs font-black">
              MAX LEVEL
            </div>
          ) : (
            <button
              data-testid="guild-hall-upgrade-btn"
              disabled={!canUpgrade || busy || costWealth <= 0 || !affordable}
              onClick={() => onUpgrade(costWealth, next)}
              title={!affordable ? `Guild pool: ${poolWealth.toLocaleString()} 💎 · need ${costWealth.toLocaleString()} 💎` : ""}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 hover:from-amber-400 hover:to-amber-600 text-black font-black text-xs tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "⏳ Upgrading…" : `⬆️ Lv ${next} · ${costWealth.toLocaleString()} 💎`}
            </button>
          )}
        </div>
      </div>
      {!canUpgrade && !maxed && (
        <div className="text-[10px] text-neutral-500 mt-2 text-right">Leader / Assistant Leader only</div>
      )}
      {!maxed && (
        <div className="text-[10px] text-neutral-500 mt-2 text-right">
          Guild pool: <span className={affordable ? "text-amber-300 font-bold" : "text-rose-400 font-bold"}>
            {poolWealth.toLocaleString()} 💎
          </span> · needs contributions from members (1,000 🪙 = 1 💎)
        </div>
      )}
    </section>
  );
}

// ───────── Page ─────────
export default function GuildHallLaddersPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyBuilding, setBusyBuilding] = useState(null);
  const [busyRush, setBusyRush] = useState(null);
  const [busyHall, setBusyHall] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());

  // 1Hz ticker for under-construction countdowns
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchState = async () => {
    const r = await axios.get(`${API}/guild/state`);
    setState(r.data || null);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await fetchState();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load guild state.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    // 🚧 Auto-poll every 15s so a teammate's upgrade & countdown surface
    // here without needing a manual refresh.
    const pollId = setInterval(() => {
      fetchState().catch(() => {});
    }, 15000);
    return () => { alive = false; clearInterval(pollId); };
  }, []);

  const handleUpgrade = async (building, cost, toLevel) => {
    const ok = await confirmDialog({
      title: `Upgrade ${building} to Lv ${toLevel}?`,
      message: `Cost: ${cost.toLocaleString()} 💎 from the guild pool`,
      confirmLabel: "Upgrade",
    });
    if (!ok) return;
    setBusyBuilding(building);
    try {
      const r = await axios.post(`${API}/guild/upgrade/${building}`);
      setState(r.data || null);
      toast.success(`🏛️ ${building[0].toUpperCase() + building.slice(1)} upgraded to Lv ${toLevel}! 🚧 Now under construction.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed.");
      try { await fetchState(); } catch { /* noop */ }
    } finally {
      setBusyBuilding(null);
    }
  };

  const handleHallUpgrade = async (costWealth, toLevel) => {
    const ok = await confirmDialog({
      title: `Raise Guild Hall to Lv ${toLevel}?`,
      message: `Cost: ${costWealth.toLocaleString()} 💎 from the GUILD POOL\n+5 member cap · unlocks Lv ${toLevel} building upgrades · group effort`,
      confirmLabel: "Upgrade",
    });
    if (!ok) return;
    setBusyHall(true);
    try {
      const r = await axios.post(`${API}/guild/hall/upgrade`);
      setState(r.data || null);
      toast.success(`🏛️ Guild Hall raised to Lv ${toLevel}! (${costWealth.toLocaleString()} 💎 from pool)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hall upgrade failed.");
      try { await fetchState(); } catch { /* noop */ }
    } finally {
      setBusyHall(false);
    }
  };

  const handleRush = async (building, odCost) => {
    if (!window.confirm(`Spend ${odCost} 💸 OD to rush the ${building} construction?`)) return;
    setBusyRush(building);
    try {
      const r = await axios.post(`${API}/guild/upgrade/${building}/rush`);
      setState(r.data || null);
      toast.success(`⏩ ${building[0].toUpperCase() + building.slice(1)} construction completed instantly!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rush failed.");
      try { await fetchState(); } catch { /* noop */ }
    } finally {
      setBusyRush(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-amber-200 flex items-center justify-center" data-testid="guild-hall-loading">
        <div className="text-sm tracking-widest opacity-60">⏳ Loading guild hall…</div>
      </div>
    );
  }

  const inGuild = state?.in_guild;
  const consts = state?.constants || {};
  const g = state?.guild || {};
  // POOLED wealth (Feb 27 2026 owner directive). Each member's contribution
  // lands in `guilds.pool_wealth`; upgrades drain from that pool.
  const poolWealth = Number(g?.pool_wealth ?? 0);
  const yourLifetimeContrib = Number(state?.your_wealth_contributed ?? 0);
  const role = state?.role;
  const canUpgrade = role === "leader" || role === "assistant_leader";

  // 🔒 GLOBAL ONE-AT-A-TIME LOCK (Feb 2026 owner directive):
  // If ANY building has a finish_at in the future, the OTHER two are
  // locked from new upgrades. The lock identifies which building owns it
  // so other cards can show "Locked — Skill Tower is busy" etc.
  const _msIfFuture = (iso) => {
    if (!iso) return 0;
    const t = new Date(iso).getTime();
    return t > nowMs ? t : 0;
  };
  const _lockMap = {
    shop:  { label: "Guild Shop",  ms: _msIfFuture(g.shop_upgrade_finish_at) },
    tower: { label: "Skill Tower", ms: _msIfFuture(g.tower_upgrade_finish_at) },
    altar: { label: "Divine Altar", ms: _msIfFuture(g.altar_upgrade_finish_at) },
  };
  const _lockedEntry = Object.entries(_lockMap).find(([, v]) => v.ms > 0);
  const globalLockBuilding = _lockedEntry ? _lockedEntry[0] : null;
  const globalLockLabel = _lockedEntry ? _lockedEntry[1].label : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0a0610] to-black text-neutral-200 pb-32" data-testid="guild-hall-ladders-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/guild" className="text-[11px] text-neutral-500 hover:text-amber-300 tracking-widest uppercase">
              ← Back to Guild
            </Link>
            <h1 className="text-3xl sm:text-4xl font-black text-amber-200 tracking-wide mt-1">
              🏛️ Guild Hall Ladders
            </h1>
            <p className="text-xs text-neutral-500 mt-1">
              Canon upgrade paths · Owner-locked Feb 27 2026
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              {inGuild ? "Pool Wealth" : "Your Wealth"}
            </div>
            <div className="text-2xl font-black text-emerald-300" data-testid="guild-hall-wealth">
              {fmt(inGuild ? poolWealth : (state?.your_wealth ?? 0))} 💎
            </div>
            {inGuild && (
              <div className="text-[9px] text-neutral-500 mt-1 text-right">
                {canUpgrade ? "👑 You can upgrade" : "Leader / Asst only"}
                {yourLifetimeContrib > 0 && (
                  <div>You: {fmt(yourLifetimeContrib)} 💎 lifetime</div>
                )}
              </div>
            )}
          </div>
        </div>

        {!inGuild && (
          <div className="rounded-xl border border-amber-800/40 bg-amber-950/20 px-4 py-3 mb-6 text-xs text-amber-200" data-testid="guild-hall-not-in-guild">
            👀 You&apos;re not in a guild yet — the ladders below show the canonical costs.
            Join or found a guild from the <Link to="/guild" className="underline">Guild page</Link> to start upgrading.
          </div>
        )}

        {/* 🏛️ Main Hall — gates every sub-building */}
        <HallCard
          hallLevel={g.hall_level ?? 1}
          towerLadder={consts.tower_ladder}
          canUpgrade={!!inGuild && canUpgrade}
          busy={busyHall}
          onUpgrade={handleHallUpgrade}
          poolWealth={poolWealth}
        />

        {/* Ladders grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LadderCard
            icon="🏪"
            title="Guild Shop"
            blurb="Unlocks high-tier materials, shards, and currencies."
            ladder={consts.shop_ladder}
            currentLevel={g.shop_level ?? 1}
            costKey="upgrade_cost_wealth"
            currentWealth={poolWealth}
            accent={{ border: "border-amber-700/60", bg: "from-amber-950/40 via-black to-amber-950/20", title: "text-amber-200", bar: "bg-amber-400", btn: "bg-amber-600 text-black" }}
            testid="guild-hall-shop-card"
            buildingId="shop"
            canUpgrade={canUpgrade}
            onUpgrade={handleUpgrade}
            busyBuilding={busyBuilding}
            finishAt={g.shop_upgrade_finish_at}
            rushOd={Number(g.shop_rush_od || 0)}
            onRush={handleRush}
            busyRush={busyRush}
            nowMs={nowMs}
            globalLockBuilding={globalLockBuilding}
            globalLockLabel={globalLockLabel}
          />
          <LadderCard
            icon="🗼"
            title="Skill Tower"
            blurb="Unlocks higher character stat-buff caps (Strength, Intellect, Defense, Stamina, Charisma, Block)."
            ladder={consts.tower_ladder}
            currentLevel={g.tower_level ?? 1}
            costKey="apex_wealth"
            currentWealth={poolWealth}
            accent={{ border: "border-cyan-700/60", bg: "from-cyan-950/40 via-black to-cyan-950/20", title: "text-cyan-200", bar: "bg-cyan-400", btn: "bg-cyan-500 text-black" }}
            testid="guild-hall-tower-card"
            buildingId="tower"
            canUpgrade={canUpgrade}
            onUpgrade={handleUpgrade}
            busyBuilding={busyBuilding}
            finishAt={g.tower_upgrade_finish_at}
            rushOd={Number(g.tower_rush_od || 0)}
            onRush={handleRush}
            busyRush={busyRush}
            nowMs={nowMs}
            globalLockBuilding={globalLockBuilding}
            globalLockLabel={globalLockLabel}
          />
          <LadderCard
            icon="🪄"
            title="Divine Altar"
            blurb="Higher-tier Blessing Wheel rewards · faster summoning cooldowns."
            ladder={consts.altar_ladder}
            currentLevel={g.altar_level ?? 1}
            costKey="upgrade_cost_wealth"
            currentWealth={poolWealth}
            accent={{ border: "border-violet-700/60", bg: "from-violet-950/40 via-black to-violet-950/20", title: "text-violet-200", bar: "bg-violet-400", btn: "bg-violet-500 text-white" }}
            testid="guild-hall-altar-card"
            buildingId="altar"
            canUpgrade={canUpgrade}
            onUpgrade={handleUpgrade}
            busyBuilding={busyBuilding}
            finishAt={g.altar_upgrade_finish_at}
            rushOd={Number(g.altar_rush_od || 0)}
            onRush={handleRush}
            busyRush={busyRush}
            nowMs={nowMs}
            globalLockBuilding={globalLockBuilding}
            globalLockLabel={globalLockLabel}
          />
          <UpkeepCard
            upkeepTable={consts.upkeep_per_week}
            currentLevel={g.hall_level ?? 1}
            currentWealth={poolWealth}
            frozen={consts.upkeep_frozen !== false}
            testid="guild-hall-upkeep-card"
          />
        </div>

        <div className="mt-8 text-center text-[10px] text-neutral-600 italic">
          Some Lv 4-10 inventories are still locked TBD — owner will fill in over time.
        </div>

        {/* 🎓 Per-member Skill Tower pointer */}
        <Link
          to="/guild/skills"
          data-testid="skill-tower-pointer"
          className="mt-6 block rounded-2xl border-2 border-cyan-700/50 bg-gradient-to-br from-[#001a20] via-black/60 to-cyan-900/15 p-5 hover:border-cyan-400 hover:shadow-[0_0_30px_-12px_rgba(34,211,238,0.7)] transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">🎓</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300/80">Train Your Stats</div>
              <div className="text-xl sm:text-2xl font-black text-cyan-200">YOUR SKILL TOWER</div>
              <p className="text-xs text-cyan-100/80 mt-1">
                💪 Power · 🧠 Intellect · 🛡️ Defense · ❤️ Stamina · 🪨 Block · ✨ Charisma · 💰 Gold
                — spend your personal Chits to permanently raise your battle rating.
                {(g.tower_level ?? 1) < 6 && (
                  <span className="block mt-1 text-amber-200 font-bold">
                    ⚠️ Tower Lv {g.tower_level ?? 1} · Block + Charisma locked until Lv 6
                  </span>
                )}
              </p>
            </div>
            <div className="text-cyan-300 text-2xl flex-shrink-0 group-hover:translate-x-1 transition-transform">→</div>
          </div>
        </Link>
      </div>
    </div>
  );
}
